import type { TravelScope } from "@/features/journey/travelPlan/currentScope";
export const CANCEL_JOURNAL_KEY = "petsoul:private-game-cancel:v1";
export type CancelSource = TravelScope & { invitationId: string; messageId: string; messageCreatedAt: string; invitationCreatedAt: string; conversationId: string };
export type CancelPending = CancelSource & { expectedVersion: number; operationId: string; phase: "pending" | "committed" };
type Store = Pick<Storage, "getItem" | "setItem">;
type Document = { schema: 1; records: CancelPending[] };
export type CancelJournalView = { kind: "clear" } | { kind: "record"; pending: CancelPending } | { kind: "held" };
const ids = ["userId","petId","householdId","homeId","invitationId","messageId","messageCreatedAt","invitationCreatedAt","conversationId"] as const;
const keys = [...ids,"householdVersion","expectedVersion","operationId","phase"].sort().join("|");
const id = (v: unknown): v is string => typeof v === "string" && v.trim() === v && v.length > 0 && v.length <= 256 && !/[\u0000-\u001f]/.test(v);
const valid = (v: unknown): v is CancelPending => {
  if (!v || typeof v !== "object" || Array.isArray(v)) return false;
  const p = v as CancelPending;
  return Object.keys(p).sort().join("|") === keys && ids.every(k => id(p[k]))
    && Number.isSafeInteger(p.householdVersion) && p.householdVersion >= 0
    && Number.isSafeInteger(p.expectedVersion) && p.expectedVersion >= 0 && p.expectedVersion < Number.MAX_SAFE_INTEGER
    && /^[A-Za-z0-9][A-Za-z0-9_.:-]{7,127}$/.test(p.operationId)
    && ["pending","committed"].includes(p.phase);
};
export const sameCancelSource = (a: CancelSource, b: CancelSource) => ids.every(k => a[k] === b[k]) && a.householdVersion === b.householdVersion;
const logical = (p: Pick<CancelSource,"userId"|"petId"|"invitationId">) => JSON.stringify([p.userId,p.petId,p.invitationId]);
function parse(raw: string | null): Document {
  if (raw === null) return { schema: 1, records: [] };
  if (raw.length > 131072) throw Error("cancel journal unavailable");
  const d = JSON.parse(raw) as Document;
  if (!d || Object.keys(d).sort().join("|") !== "records|schema" || d.schema !== 1 || !Array.isArray(d.records)
    || d.records.length > 128 || !d.records.every(valid) || new Set(d.records.map(logical)).size !== d.records.length) throw Error("cancel journal unavailable");
  return d;
}
/** Durable metadata only. A stored record blocks writes; it never proves a result or permission. */
export function createCancelJournal(storage: () => Store = () => window.localStorage) {
  const read = () => parse(storage().getItem(CANCEL_JOURNAL_KEY));
  const write = (document: Document) => {
    const raw = JSON.stringify(document); storage().setItem(CANCEL_JOURNAL_KEY,raw);
    if (storage().getItem(CANCEL_JOURNAL_KEY) !== raw) throw Error("cancel journal not durable");
    read();
  };
  return {
    inspect(source: Pick<CancelSource,"userId"|"petId"|"invitationId"|"householdId"|"homeId"|"messageId"|"messageCreatedAt">): CancelJournalView {
      try {
        const p = read().records.find(p => logical(p) === logical(source));
        if (!p) return { kind: "clear" };
        return ["householdId","homeId","messageId","messageCreatedAt"].every(k => p[k as keyof CancelPending] === source[k as keyof typeof source])
          ? {kind:"record",pending:Object.freeze({...p})} : {kind:"held"};
      } catch { return {kind:"held"}; }
    },
    begin(source: CancelSource, expectedVersion: number, operationId: string): CancelPending {
      const pending: CancelPending = {...source,expectedVersion,operationId,phase:"pending"};
      if (!valid(pending)) throw Error("cancel metadata invalid");
      const document = read();
      if (document.records.length >= 128 || document.records.some(p => logical(p) === logical(source))) throw Error("original cancel already registered");
      write({schema:1,records:[...document.records,pending]});
      return Object.freeze(pending);
    },
    committed(pending: CancelPending) {
      const document = read(), original = document.records.find(p => p.operationId === pending.operationId && sameCancelSource(p,pending) && p.expectedVersion === pending.expectedVersion);
      if (!original) throw Error("original cancel missing");
      write({schema:1,records:document.records.map(p => p === original ? {...p,phase:"committed"} : p)});
    },
  };
}
export async function withCancelJournalLock<T>(operation: () => Promise<T>): Promise<T> {
  if (!navigator.locks?.request) throw Error("safe local cancel locking unavailable");
  return navigator.locks.request("petsoul-cancel-document:"+CANCEL_JOURNAL_KEY,{mode:"exclusive"},operation);
}
